import { useState, useRef, useEffect } from "react"
import { Check, ChevronDown, Search } from "lucide-react"
import { cn } from "../../lib/utils"


// Convert country code to emoji flag
const getFlagEmoji = (countryCode) => {
  if (!countryCode) return ""
  const codePoints = countryCode
    .toUpperCase()
    .split("")
    .map((char) => 127397 + char.charCodeAt(0))
  return String.fromCodePoint(...codePoints)
}

// List of countries with codes and names
const countries = [
  { code: "us", name: "United States", flag: getFlagEmoji("us") },
  { code: "ca", name: "Canada", flag: getFlagEmoji("ca") },
  { code: "gb", name: "United Kingdom", flag: getFlagEmoji("gb") },
  { code: "au", name: "Australia", flag: getFlagEmoji("au") },
  { code: "de", name: "Germany", flag: getFlagEmoji("de") },
  { code: "fr", name: "France", flag: getFlagEmoji("fr") },
  { code: "jp", name: "Japan", flag: getFlagEmoji("jp") },
  { code: "cn", name: "China", flag: getFlagEmoji("cn") },
  { code: "in", name: "India", flag: getFlagEmoji("in") },
  { code: "br", name: "Brazil", flag: getFlagEmoji("br") },
  { code: "mx", name: "Mexico", flag: getFlagEmoji("mx") },
  { code: "it", name: "Italy", flag: getFlagEmoji("it") },
  { code: "es", name: "Spain", flag: getFlagEmoji("es") },
  { code: "kr", name: "South Korea", flag: getFlagEmoji("kr") },
  { code: "ru", name: "Russia", flag: getFlagEmoji("ru") },
  { code: "nl", name: "Netherlands", flag: getFlagEmoji("nl") },
  { code: "se", name: "Sweden", flag: getFlagEmoji("se") },
  { code: "no", name: "Norway", flag: getFlagEmoji("no") },
  { code: "dk", name: "Denmark", flag: getFlagEmoji("dk") },
  { code: "fi", name: "Finland", flag: getFlagEmoji("fi") },
  { code: "sg", name: "Singapore", flag: getFlagEmoji("sg") },
  { code: "ae", name: "United Arab Emirates", flag: getFlagEmoji("ae") },
  { code: "sa", name: "Saudi Arabia", flag: getFlagEmoji("sa") },
  { code: "za", name: "South Africa", flag: getFlagEmoji("za") },
  { code: "ng", name: "Nigeria", flag: getFlagEmoji("ng") },
  { code: "eg", name: "Egypt", flag: getFlagEmoji("eg") },
  { code: "ar", name: "Argentina", flag: getFlagEmoji("ar") },
  { code: "co", name: "Colombia", flag: getFlagEmoji("co") },
  { code: "cl", name: "Chile", flag: getFlagEmoji("cl") },
  { code: "pe", name: "Peru", flag: getFlagEmoji("pe") },
]

function SelectCountry({ id, label, value, onChange, error, className }) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const dropdownRef = useRef(null)
  const searchRef = useRef(null)

  const selectedCountry = countries.find((country) => country.code === value)

  const filteredCountries = countries.filter((country) => country.name.toLowerCase().includes(searchTerm.toLowerCase()))

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus()
    }
  }, [isOpen])

  return (
    <div className="space-y-2">
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-neutral-700 text-start">
          {label}
        </label>
      )}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          className={cn(
            "flex w-full items-center justify-between w-full px-3 py-2 border border-neutral-200 rounded-lg h-12 text-black placeholder-neutral-400 text-sm focus:outline-none focus:border-black",
            "focus:border-black focus:outline-none focus:ring-none focus:ring-none",
            "hover:border-neutral-400",
            "disabled:cursor-not-allowed disabled:opacity-50",
            error && "border-red-500 focus:border-red-500 focus:ring-red-500",
            className,
          )}
          onClick={() => setIsOpen(!isOpen)}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          id={id}
        >
          {selectedCountry ? (
            <div className="flex items-center">
              <span className="mr-2 text-lg">{selectedCountry.flag}</span>
              <span>{selectedCountry.name}</span>
            </div>
          ) : (
            <span className="text-neutral-500">Select your country</span>
          )}
          <ChevronDown className="h-4 w-4 text-neutral-500" />
        </button>

        {isOpen && (
          <div className="absolute z-10 mt-1 w-full rounded-md border border-neutral-200 bg-white shadow-lg">
            <div className="p-2 border-b border-neutral-200">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                <input
                  ref={searchRef}
                  type="text"
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg h-10 pl-8 text-black placeholder-neutral-400 text-sm focus:outline-none focus:border-black"
                  placeholder="Search countries..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <ul className="max-h-60 overflow-auto py-1" role="listbox" aria-labelledby={id}>
              {filteredCountries.length > 0 ? (
                filteredCountries.map((country) => (
                  <li
                    key={country.code}
                    className={cn(
                      "flex items-center px-3 py-2 cursor-pointer",
                      "hover:bg-neutral-100",
                      value === country.code && "bg-neutral-100",
                    )}
                    onClick={() => {
                      onChange(country.code)
                      setIsOpen(false)
                      setSearchTerm("")
                    }}
                    role="option"
                    aria-selected={value === country.code}
                  >
                    <span className="mr-2 text-lg">{country.flag}</span>
                    <span className="flex-grow text-sm text-neutral-700">{country.name}</span>
                    {value === country.code && <Check className="h-4 w-4 text-black" />}
                  </li>
                ))
              ) : (
                <li className="px-3 py-2 text-neutral-500 text-center text-xs">No countries found</li>
              )}
            </ul>
          </div>
        )}
      </div>
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  )
}

export default SelectCountry